<script setup lang="ts">
interface SavedAddress {
  id: string
  address1: string
  address2: string | null
  city: string
  province: string
  zip: string
  country: string
  isDefault: boolean
}

const { cartLines, cartTotal, cartId } = useCart()
const { customer, isLoggedIn } = useAuth()
const config = useRuntimeConfig()

useHead({
  script: [{ src: 'https://js.stripe.com/v3/', async: true }],
})

const form = reactive({
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: 'United States',
})

watchEffect(() => {
  if (isLoggedIn.value && customer.value) {
    if (!form.email && customer.value.email) form.email = customer.value.email
    if (!form.firstName && customer.value.firstName)
      form.firstName = customer.value.firstName
    if (!form.lastName && customer.value.lastName)
      form.lastName = customer.value.lastName
  }
})

// Saved addresses
const savedAddresses = ref<SavedAddress[]>([])
const selectedAddressId = ref<string | 'new' | null>(null)

function applyAddress(addr: SavedAddress) {
  selectedAddressId.value = addr.id
  form.address = addr.address1 ?? ''
  form.city = addr.city ?? ''
  form.state = addr.province ?? ''
  form.zip = addr.zip ?? ''
  form.country = addr.country ?? 'United States'
}

function selectNew() {
  selectedAddressId.value = 'new'
  form.address = ''
  form.city = ''
  form.state = ''
  form.zip = ''
  form.country = 'United States'
}

const submitting = ref(false)
const errorMsg = ref('')
const cardError = ref('')
const fieldErrors = reactive<Record<string, string>>({})

let stripe: any = null
let cardElement: any = null

onMounted(async () => {
  // Fetch saved addresses for logged-in users
  if (isLoggedIn.value) {
    try {
      const addresses = await $fetch<SavedAddress[]>('/api/customer/addresses')
      savedAddresses.value = addresses
      if (addresses.length > 0) {
        const def = addresses.find((a) => a.isDefault) ?? addresses[0]
        applyAddress(def)
      }
    } catch {
      // Not logged in or fetch failed — fall through to manual form
    }
  }

  // Wait for Stripe.js to load
  await new Promise<void>((resolve) => {
    if ((window as any).Stripe) return resolve()
    const check = setInterval(() => {
      if ((window as any).Stripe) {
        clearInterval(check)
        resolve()
      }
    }, 50)
  })

  stripe = (window as any).Stripe(config.public.stripePublishableKey)
  const elements = stripe.elements()
  cardElement = elements.create('card', {
    style: {
      base: {
        fontFamily: "'Poppins', sans-serif",
        fontSize: '14px',
        color: '#1c1a16',
        '::placeholder': { color: '#a09080' },
      },
      invalid: { color: '#ef4444' },
    },
  })
  cardElement.mount('#card-element')
  cardElement.on('change', (e: any) => {
    cardError.value = e.error ? e.error.message : ''
  })
})

function validate() {
  const required: { key: keyof typeof form; label: string }[] = [
    { key: 'email', label: 'Email' },
    { key: 'firstName', label: 'First name' },
    { key: 'lastName', label: 'Last name' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'zip', label: 'ZIP' },
  ]

  let valid = true
  for (const { key, label } of required) {
    if (!form[key]?.trim()) {
      fieldErrors[key] = `${label} is required`
      valid = false
    } else {
      delete fieldErrors[key]
    }
  }

  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    fieldErrors.email = 'Enter a valid email address'
    valid = false
  }

  return valid
}

async function placeOrder() {
  errorMsg.value = ''
  cardError.value = ''

  if (!validate()) return
  if (!cartId.value) {
    errorMsg.value = 'Your cart is empty.'
    return
  }
  if (!stripe || !cardElement) {
    errorMsg.value = 'Payment form is not ready. Please refresh and try again.'
    return
  }

  submitting.value = true
  try {
    // 1. Create PaymentIntent server-side
    const { clientSecret } = await $fetch<{ clientSecret: string }>(
      '/api/checkout/create-payment-intent',
      {
        method: 'POST',
      },
    )

    // 2. Confirm card payment client-side
    const { paymentIntent, error } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${form.firstName} ${form.lastName}`,
            email: form.email,
            phone: form.phone || undefined,
            address: {
              line1: form.address,
              city: form.city,
              state: form.state || undefined,
              postal_code: form.zip,
              country: 'US',
            },
          },
        },
      },
    )

    if (error) {
      cardError.value = error.message ?? 'Payment failed. Please try again.'
      return
    }

    // 3. Confirm order server-side (creates Shopify order + clears cart cookie)
    await $fetch('/api/checkout/confirm-order', {
      method: 'POST',
      body: { paymentIntentId: paymentIntent.id, ...form },
    })

    await navigateTo('/order-confirmed')
  } catch (err: any) {
    errorMsg.value =
      err?.data?.statusMessage ?? 'Something went wrong. Please try again.'
  } finally {
    submitting.value = false
  }
}

function fieldClass(key: string) {
  return fieldErrors[key]
    ? 'bg-surface border border-error h-11 font-sans text-sm text-dark placeholder:text-muted px-3.5 rounded-lg outline-none transition-colors w-full'
    : 'bg-surface border border-stroke h-11 font-sans text-sm text-dark placeholder:text-muted px-3.5 rounded-lg outline-none focus:border-brand transition-colors w-full'
}
</script>

<template>
  <div class="bg-page flex flex-col items-start relative">
    <!-- Page Header -->
    <div
      class="flex flex-col gap-2 items-start pb-8 pt-14 px-4 md:px-20 w-full"
    >
      <p class="font-sans text-[13px] text-muted">
        <NuxtLink to="/" class="hover:text-secondary transition-colors"
          >Home</NuxtLink
        >
        &nbsp;/&nbsp;
        <NuxtLink to="/cart" class="hover:text-secondary transition-colors"
          >Cart</NuxtLink
        >
        &nbsp;/&nbsp; Checkout
      </p>
      <p class="font-serif font-semibold text-4xl md:text-[42px] text-dark">
        Checkout
      </p>
    </div>

    <!-- Checkout Body -->
    <div
      class="flex flex-col md:flex-row gap-12 items-start pb-24 pt-2 px-4 md:px-20 w-full"
    >
      <!-- Left — Form -->
      <div class="flex flex-1 flex-col gap-10 items-start w-full">
        <!-- Contact -->
        <div class="flex flex-col gap-4 items-start w-full">
          <p class="font-serif font-semibold text-xl text-dark">Contact</p>
          <div class="flex flex-col md:flex-row gap-3 w-full">
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary"
                >Email *</label
              >
              <input
                v-model="form.email"
                type="email"
                placeholder="you@example.com"
                :class="fieldClass('email')"
              />
              <p v-if="fieldErrors.email" class="font-sans text-xs text-error">
                {{ fieldErrors.email }}
              </p>
            </div>
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary"
                >Phone</label
              >
              <input
                v-model="form.phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                class="bg-surface border border-stroke h-11 font-sans text-sm text-dark placeholder:text-muted px-3.5 rounded-lg outline-none focus:border-brand transition-colors w-full"
              />
            </div>
          </div>
        </div>

        <!-- Divider -->
        <div class="bg-stroke h-px w-full" />

        <!-- Shipping Address -->
        <div class="flex flex-col gap-4 items-start w-full">
          <p class="font-serif font-semibold text-xl text-dark">
            Shipping Address
          </p>

          <!-- Saved address picker (logged-in users with saved addresses) -->
          <div
            v-if="savedAddresses.length > 0"
            class="flex flex-col gap-2 w-full"
          >
            <!-- Existing address cards -->
            <button
              v-for="addr in savedAddresses"
              :key="addr.id"
              type="button"
              class="flex gap-3.5 items-start px-4 py-3.5 rounded-lg border w-full text-left transition-colors"
              :class="
                selectedAddressId === addr.id
                  ? 'bg-surface border-brand'
                  : 'bg-surface border-stroke hover:border-brand/50'
              "
              @click="applyAddress(addr)"
            >
              <!-- Radio dot -->
              <div
                class="mt-0.5 shrink-0 size-[18px] rounded-full border-2 flex items-center justify-center transition-colors"
                :class="
                  selectedAddressId === addr.id
                    ? 'border-brand'
                    : 'border-stroke'
                "
              >
                <div
                  v-if="selectedAddressId === addr.id"
                  class="bg-brand rounded-full size-2"
                />
              </div>
              <div class="flex flex-1 flex-col gap-0.5 min-w-0">
                <p class="font-sans font-medium text-sm text-dark">
                  {{ addr.address1 }}
                  <span
                    v-if="addr.isDefault"
                    class="ml-2 font-label text-[11px] text-brand bg-primary-subtle px-1.5 py-0.5 rounded"
                    >Default</span
                  >
                </p>
                <p class="font-sans text-xs text-muted">
                  {{ addr.city
                  }}<span v-if="addr.province">, {{ addr.province }}</span>
                  {{ addr.zip }}
                </p>
                <p class="font-sans text-xs text-muted">{{ addr.country }}</p>
              </div>
            </button>

            <!-- Add new address option -->
            <button
              type="button"
              class="flex gap-3.5 items-center px-4 py-3.5 rounded-lg border w-full text-left transition-colors"
              :class="
                selectedAddressId === 'new'
                  ? 'bg-surface border-brand'
                  : 'bg-surface border-stroke hover:border-brand/50'
              "
              @click="selectNew"
            >
              <div
                class="mt-0.5 shrink-0 size-[18px] rounded-full border-2 flex items-center justify-center transition-colors"
                :class="
                  selectedAddressId === 'new' ? 'border-brand' : 'border-stroke'
                "
              >
                <div
                  v-if="selectedAddressId === 'new'"
                  class="bg-brand rounded-full size-2"
                />
              </div>
              <div class="flex items-center gap-2">
                <svg
                  class="size-4 text-secondary shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
                <p class="font-sans text-sm text-dark">Add a new address</p>
              </div>
            </button>
          </div>

          <!-- Address form — always shown for new address; hidden when an existing is selected -->
          <div
            v-if="savedAddresses.length === 0 || selectedAddressId === 'new'"
            class="flex flex-col gap-3 w-full"
            :class="{ 'mt-1': savedAddresses.length > 0 }"
          >
            <div class="flex flex-col md:flex-row gap-3 w-full">
              <div class="flex flex-col gap-1.5 flex-1">
                <label class="font-label font-light text-[13px] text-secondary"
                  >First Name *</label
                >
                <input
                  v-model="form.firstName"
                  type="text"
                  placeholder="Jane"
                  :class="fieldClass('firstName')"
                />
                <p
                  v-if="fieldErrors.firstName"
                  class="font-sans text-xs text-error"
                >
                  {{ fieldErrors.firstName }}
                </p>
              </div>
              <div class="flex flex-col gap-1.5 flex-1">
                <label class="font-label font-light text-[13px] text-secondary"
                  >Last Name *</label
                >
                <input
                  v-model="form.lastName"
                  type="text"
                  placeholder="Doe"
                  :class="fieldClass('lastName')"
                />
                <p
                  v-if="fieldErrors.lastName"
                  class="font-sans text-xs text-error"
                >
                  {{ fieldErrors.lastName }}
                </p>
              </div>
            </div>
            <div class="flex flex-col gap-1.5 w-full">
              <label class="font-label font-light text-[13px] text-secondary"
                >Address *</label
              >
              <input
                v-model="form.address"
                type="text"
                placeholder="123 Main Street"
                :class="fieldClass('address')"
              />
              <p
                v-if="fieldErrors.address"
                class="font-sans text-xs text-error"
              >
                {{ fieldErrors.address }}
              </p>
            </div>
            <div class="flex flex-col md:flex-row gap-3 w-full">
              <div class="flex flex-col gap-1.5 flex-1">
                <label class="font-label font-light text-[13px] text-secondary"
                  >City *</label
                >
                <input
                  v-model="form.city"
                  type="text"
                  placeholder="Los Angeles"
                  :class="fieldClass('city')"
                />
                <p v-if="fieldErrors.city" class="font-sans text-xs text-error">
                  {{ fieldErrors.city }}
                </p>
              </div>
              <div class="flex flex-col gap-1.5 w-[120px] shrink-0">
                <label class="font-label font-light text-[13px] text-secondary"
                  >State</label
                >
                <input
                  v-model="form.state"
                  type="text"
                  placeholder="CA"
                  class="bg-surface border border-stroke h-11 font-sans text-sm text-dark placeholder:text-muted px-3.5 rounded-lg outline-none focus:border-brand transition-colors w-full"
                />
              </div>
              <div class="flex flex-col gap-1.5 w-[120px] shrink-0">
                <label class="font-label font-light text-[13px] text-secondary"
                  >ZIP *</label
                >
                <input
                  v-model="form.zip"
                  type="text"
                  placeholder="90210"
                  :class="fieldClass('zip')"
                />
                <p v-if="fieldErrors.zip" class="font-sans text-xs text-error">
                  {{ fieldErrors.zip }}
                </p>
              </div>
            </div>
            <div class="flex flex-col gap-1.5 w-full">
              <label class="font-label font-light text-[13px] text-secondary"
                >Country</label
              >
              <select
                v-model="form.country"
                class="bg-surface border border-stroke h-11 font-sans text-sm text-dark px-3.5 rounded-lg outline-none focus:border-brand transition-colors w-full appearance-none"
              >
                <option>United States</option>
                <option>Canada</option>
                <option>United Kingdom</option>
                <option>Australia</option>
              </select>
            </div>
          </div>

          <!-- Selected address summary (when an existing address is chosen) -->
          <div
            v-else-if="selectedAddressId && selectedAddressId !== 'new'"
            class="flex flex-col gap-3 w-full"
          >
            <!-- Name fields still editable -->
            <div class="flex flex-col md:flex-row gap-3 w-full">
              <div class="flex flex-col gap-1.5 flex-1">
                <label class="font-label font-light text-[13px] text-secondary"
                  >First Name *</label
                >
                <input
                  v-model="form.firstName"
                  type="text"
                  placeholder="Jane"
                  :class="fieldClass('firstName')"
                />
                <p
                  v-if="fieldErrors.firstName"
                  class="font-sans text-xs text-error"
                >
                  {{ fieldErrors.firstName }}
                </p>
              </div>
              <div class="flex flex-col gap-1.5 flex-1">
                <label class="font-label font-light text-[13px] text-secondary"
                  >Last Name *</label
                >
                <input
                  v-model="form.lastName"
                  type="text"
                  placeholder="Doe"
                  :class="fieldClass('lastName')"
                />
                <p
                  v-if="fieldErrors.lastName"
                  class="font-sans text-xs text-error"
                >
                  {{ fieldErrors.lastName }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Divider -->
        <div class="bg-stroke h-px w-full" />

        <!-- Shipping Method -->
        <div class="flex flex-col gap-4 items-start w-full">
          <p class="font-serif font-semibold text-xl text-dark">
            Shipping Method
          </p>
          <label
            class="bg-surface border border-brand flex gap-3.5 items-center px-4 py-3.5 rounded-lg w-full cursor-pointer"
          >
            <div
              class="bg-brand flex items-center justify-center rounded-full shrink-0 size-[18px]"
            >
              <div class="bg-white rounded-full size-[7px]" />
            </div>
            <div class="flex flex-1 flex-col gap-0.5">
              <p class="font-sans font-medium text-sm text-dark">
                Standard Shipping
              </p>
              <p class="font-sans text-xs text-muted">5–7 business days</p>
            </div>
            <p class="font-sans font-semibold text-sm text-brand">Free</p>
          </label>
        </div>

        <!-- Divider -->
        <div class="bg-stroke h-px w-full" />

        <!-- Payment -->
        <div class="flex flex-col gap-3 items-start w-full">
          <p class="font-serif font-semibold text-xl text-dark">Payment</p>
          <div
            class="bg-surface border border-stroke rounded-lg px-4 py-3.5 w-full"
          >
            <div id="card-element" class="py-1" />
            <p v-if="cardError" class="font-sans text-xs text-error mt-2">
              {{ cardError }}
            </p>
          </div>
          <p class="font-sans text-xs text-muted flex items-center gap-1.5">
            <svg
              class="size-3.5 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
              />
            </svg>
            Secured by Stripe — your card details are encrypted
          </p>
        </div>

        <!-- Error message -->
        <div
          v-if="errorMsg"
          class="bg-error/10 border border-error flex items-center gap-2.5 px-4 py-3 rounded-lg w-full"
        >
          <p class="font-sans text-[13px] text-error">{{ errorMsg }}</p>
        </div>

        <!-- Place Order -->
        <button
          class="bg-primary flex gap-1 items-center justify-center px-3 py-2 w-full disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
          :disabled="submitting"
          @click="placeOrder"
        >
          <span class="font-label text-base text-white leading-6">
            {{ submitting ? 'Processing…' : 'Place Order' }}
          </span>
          <svg
            v-if="!submitting"
            class="size-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>

      <!-- Right — Order Summary -->
      <div
        class="bg-surface flex flex-col items-start px-7 py-8 rounded-xl shrink-0 w-full md:w-[368px]"
      >
        <p class="font-serif font-semibold text-[22px] text-dark">
          Order Summary
        </p>
        <div class="h-5" />

        <!-- Items -->
        <div class="flex flex-col w-full">
          <div
            v-for="(line, index) in cartLines"
            :key="line.id"
            class="flex gap-3.5 items-center py-3.5 w-full"
            :class="{ 'border-t border-stroke': index > 0 }"
          >
            <div
              class="rounded-md shrink-0 size-14 bg-raised overflow-hidden flex items-center justify-center"
            >
              <img
                v-if="line.image"
                :src="line.image"
                :alt="line.productTitle"
                class="object-cover size-full rounded-md"
              />
              <div v-else class="size-full bg-raised rounded-md" />
            </div>
            <div class="flex flex-1 flex-col gap-[3px] min-w-0">
              <p
                class="font-sans font-medium text-sm text-dark truncate w-full"
              >
                {{ line.productTitle }}
              </p>
              <p class="font-sans text-xs text-muted">
                {{ line.variantTitle }}
              </p>
              <p class="font-sans text-xs text-secondary">
                Qty {{ line.quantity }}
              </p>
            </div>
            <p class="font-sans font-semibold text-[15px] text-dark shrink-0">
              {{ line.price }}
            </p>
          </div>
          <p
            v-if="cartLines.length === 0"
            class="font-sans text-sm text-muted py-4"
          >
            No items in cart.
          </p>
        </div>

        <div class="h-2" />
        <div class="bg-stroke h-px w-full" />
        <div class="h-4" />

        <!-- Shipping -->
        <div class="flex items-center justify-between py-1.5 text-sm w-full">
          <p class="font-sans text-secondary">Shipping</p>
          <p class="font-sans font-medium text-brand">Free</p>
        </div>

        <div class="h-1.5" />
        <div class="bg-stroke h-px w-full" />
        <div class="h-3" />

        <!-- Total -->
        <div
          class="flex font-sans font-semibold items-center justify-between text-dark w-full"
        >
          <p class="text-base">Total</p>
          <p class="text-xl">{{ cartTotal }}</p>
        </div>
      </div>
    </div>

  </div>
</template>
